import React, { useState, useEffect } from "react";
import { useStateSelector } from "utils/field";
import isEmpty from "lodash/isEmpty";
import classNames from "classnames";

import { getBaseUnits, getUnitOptions } from "utils/fields";
import { getDecimalPlaces, getValidation } from "utils/fieldValidation";

import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import Checkbox from "components/Input/Checkbox";
import PVSystem from "./PVSystem";
import AddSystem from "./AddSystem";
import EmptyState from "components/EmptyState";
import Field from "components/Field";

import sharedClasses from "features/Model/sharedStyles.module.scss";
import classes from "./style.module.scss";

const Generation = ({ accessor, change, isUpgrade = false, canBeEditedFromParametric = true }) => {
    const pvSystems = useStateSelector(`${accessor}.pvSystems`) || {};
    const windEnergyUnits = useStateSelector(`${accessor}.windEnergyContribution_u`);
    const pvCapacityValue = useStateSelector(`${accessor}.pvCapacity`);
    const pvCapacityUnits = useStateSelector(`${accessor}.pvCapacity_u`);

    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};
    const { major: h2kMajor, minor: h2kMinor = 11 } = useStateSelector("model.modelData.h2kVersion") || {};

    const [azimSubmitting, setAzimSubmitting] = useState(false);
    const numSystems = Object.keys(pvSystems).length;

    useEffect(() => {
        let overwriteCapacity = true;
        const totalCapacity = Object.keys(pvSystems).reduce((sum, id) => {
            const { array: { capacity = 0, area = 0, count = 0, width = 0, height = 0 } = {} } = pvSystems[id] || {};

            const calcArea = parseFloat((count * width * height).toFixed(2));

            if (parseFloat(area.toFixed(2)) !== calcArea) {
                overwriteCapacity = false;
            }

            return sum + capacity;
        }, 0);

        if (overwriteCapacity && !isNaN(totalCapacity) && totalCapacity > 0 && parseInt(h2kMinor) >= 11) {
            change(`${accessor}.pvCapacity`, parseFloat(totalCapacity.toFixed(2)));
        }
    }, [pvSystems]);

    return (
        <div className={classNames({[sharedClasses.section]: !isUpgrade})}>
            <div className={classes.heading}>
                <div className={classes.title}>
                    <h4>Enter data for on-site renewable energy generation.</h4>
                </div>
            </div>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.windEnergyContribution`}
                    fieldKey={"windContribution"}
                    label="Annual Wind Energy"
                    placeholder={0.0}
                    decimals={getDecimalPlaces("windContribution")}
                    units={{
                        base: getBaseUnits("windContribution", modelUnits),
                        options: getUnitOptions("energy"),
                        selected: windEnergyUnits,
                        unitType: "energy",
                        accessor: `${accessor}.windEnergyContribution_u`,
                    }}
                    disabled={!canBeEditedFromParametric}
                />
                {h2kMinor >= 11 && (
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={`${accessor}.pvCapacity`}
                        label="PV System Capacity"
                        placeholder={0.0}
                        fieldKey={"pvSystemCapacity"}
                        decimals={getDecimalPlaces("pvSystemCapacity")}
                        setValue={pvCapacityValue}
                        units={{
                            base: getBaseUnits("pvSystemCapacity", modelUnits),
                            options: ["kW", "W"],
                            selected: pvCapacityUnits,
                            unitType: "power",
                            accessor: `${accessor}.pvCapacity_u`,
                        }}
                        disabled={!canBeEditedFromParametric}
                    />
                )}
                <Field
                    component={Checkbox}
                    name={`${accessor}.solarReady`}
                    label="Solar Ready"
                    large
                    className={classes.checkbox}
                    type="checkbox"
                    disabled={!canBeEditedFromParametric}
                />
                {h2kMinor >= 11 && (
                    <Field
                        component={Checkbox}
                        name={`${accessor}.hasBatteryStorage`}
                        label="Battery Storage"
                        large
                        className={classes.checkbox}
                        type="checkbox"
                        disabled={!canBeEditedFromParametric}
                    />
                )}
            </InputRow>
            <div className={classes.title}>
                <h4>Photovoltaic Systems</h4>
                <AddSystem accessor={accessor} numSystems={numSystems} change={change} />
            </div>
            {isEmpty(pvSystems) && <EmptyState title="No photovoltaic systems to display" style={{}} />}
            {!isEmpty(pvSystems) &&
                Object.keys(pvSystems).map((id) => (
                    <PVSystem
                        accessor={`${accessor}.pvSystems.${id}`}
                        change={change}
                        key={id}
                        isUpgrade={isUpgrade}
                        setAzimSubmitting={setAzimSubmitting}
                        azimSubmitting={azimSubmitting}
                    />
                ))}
        </div>
    );
};

export default Generation;
